import { Building2, Globe, Users, CalendarIcon, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useFetcher } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'

interface CompanyInfo {
	name: string
	image?: string
	founded?: string
	employees?: string
	website?: string
	description?: string
}

export function CompanySearchResults({ 
	companyName, 
	selectedDate,
	onCompanySelect
}: { 
	companyName: string
	selectedDate: Date
	onCompanySelect?: (company: CompanyInfo) => void
}) {
	const fetcher = useFetcher<{ companies: CompanyInfo[] }>()
	const [searchTerm, setSearchTerm] = useState('')
	const [currentIndex, setCurrentIndex] = useState(0)
	
	useEffect(() => {
		if (companyName.length > 2 && companyName !== searchTerm) {
			const timer = setTimeout(() => {
				setSearchTerm(companyName)
				setCurrentIndex(0) // Reset to first result on new search
				void fetcher.load(`/api/company-search?q=${encodeURIComponent(companyName)}`)
			}, 1000)
			return () => clearTimeout(timer)
		}
	}, [companyName, searchTerm, fetcher])

	// Check if we have data
	const companies = fetcher.data?.companies || []
	const company = companies[currentIndex]
	
	// Notify parent when company changes
	useEffect(() => {
		if (company && onCompanySelect) {
			onCompanySelect(company)
		}
	}, [company, onCompanySelect])

	// Don't show anything if name too short
	if (companyName.length <= 2) return null

	// Loading state
	if (fetcher.state === 'loading') {
		return (
			<div className="mt-8 p-6 border rounded-lg bg-card">
				<div className="flex items-center justify-center gap-2">
					<Loader2 className="h-4 w-4 animate-spin" />
					<span>Zoeken...</span>
				</div>
			</div>
		)
	}
	
	// No results
	if (searchTerm && companies.length === 0) {
		return (
			<div className="mt-8 p-6 border rounded-lg bg-card">
				<p className="text-center text-muted-foreground">
					Geen resultaten voor "{searchTerm}"
				</p>
			</div>
		)
	}
	
	if (!company) return null

	const hasMultipleResults = companies.length > 1
	const canGoPrevious = currentIndex > 0
	const canGoNext = currentIndex < companies.length - 1

	return (
		<div className="mt-8 space-y-4">
			<div className="p-6 border rounded-lg bg-card relative">
				{/* Navigation buttons */}
				{hasMultipleResults && (
					<div className="absolute top-4 right-4 flex gap-2">
						<Button
							variant="outline"
							size="icon"
							onClick={() => setCurrentIndex(currentIndex - 1)}
							disabled={!canGoPrevious}
							className="h-8 w-8"
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<Button
							variant="outline"
							size="icon"
							onClick={() => setCurrentIndex(currentIndex + 1)}
							disabled={!canGoNext}
							className="h-8 w-8"
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				)}
				
				<div className="flex gap-4">
					<div className="w-16 h-16 rounded bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
						{company.image ? (
							<img 
								src={company.image} 
								alt={company.name}
								className="w-full h-full object-contain"
							/>
						) : (
							<Building2 className="w-8 h-8 text-muted-foreground" />
						)}
					</div>
					<div className="flex-1 pr-20">
						<h3 className="text-lg font-semibold font-[family-name:var(--font-heading)]">{company.name}</h3>
						{company.description && (
							<p className="text-sm text-muted-foreground mt-1">{company.description}</p>
						)}
						<div className="flex gap-4 mt-2 text-sm">
							{company.founded && <span>Opgericht: {company.founded}</span>}
							{company.employees && <span>{company.employees} medewerkers</span>}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}